import * as THREE from 'three';

/**
 * The time of day, and everything the light does with it.
 *
 * The clock belongs to the **server** — two players standing beside each other
 * have to see the same dusk — and arrives once, in `welcome`, as a phase and
 * the length of a day. From there this runs on the browser's own timer: a day
 * is three hours, so a second of drift is nothing, and a reconnect resets it
 * anyway.
 *
 * # Where the sun is
 *
 * Daylight takes two thirds of the cycle and night the last third. The sun
 * climbs a half-circle across the daylight part — up at dawn, overhead at noon,
 * down at dusk — and the moon does the same across the night, from the opposite
 * corner of the sky. Splitting the cycle by **time** rather than by the sun's
 * angle is what keeps "two hours of day, one of night" exactly true; deriving
 * the split from an orbit would have made it whatever the geometry decided.
 *
 * # What it does not do
 *
 * Nothing but light. No visibility rules, no movement, no spawning by the
 * clock — the server does not read the phase for anything, and that is a
 * decision recorded here rather than an oversight.
 */

/** Highest the sun gets, in degrees. Not 90°: a vertical sun casts no shadow. */
const SUN_PEAK = 62;

/** Highest the moon gets. Lower than the sun, so night reads as a different sky. */
const MOON_PEAK = 40;

/** How far round the horizon the light travels, in degrees, rise to set. */
const SUN_SWEEP = 150;

/** Where that arc starts, measured from the world's +X axis. */
const SUN_START = -110;

/**
 * Below this angle the shadow is faded out.
 *
 * A sun on the horizon throws a shadow tens of units long — a hill of twenty
 * levels reaches 28 units at 10° and 57 at 5° — and at that grazing angle the
 * depth comparison breaks down into acne no bias can cover. Dawn and dusk fade
 * their shadows instead, which is also what they look like.
 */
const SHADOW_FADE_DEGREES = 12;

export interface DaylightClock {
  /** 0 at sunrise, 1 at the next one. */
  phase: number;
  daySeconds: number;
  nightShare: number;
  /**
   * Whether the day is being held by an administrator.
   *
   * The client runs its own timer between messages, so it has to be told to
   * stop running it — a held sun that kept advancing here would drift away
   * from the server's within a minute of being pinned.
   */
  paused?: boolean;
}

/** What the sky looks like at one moment. */
export interface SkyState {
  /** Unit vector towards the light, in world space. */
  direction: THREE.Vector3;
  /** Degrees above the horizon. */
  altitude: number;
  sunColor: THREE.Color;
  sunIntensity: number;
  /** Sky and ground halves of the ambient term. */
  skyColor: THREE.Color;
  groundColor: THREE.Color;
  ambientIntensity: number;
  fogColor: THREE.Color;
  /** How much of the shadow is drawn, 0 at a grazing angle. */
  shadowStrength: number;
  /** True while the moon rather than the sun is the light. */
  night: boolean;
  /**
   * Ile jest nocy, 0–1, przez oba przejścia.
   *
   * Obok `night`, a nie zamiast: tamto jest pytaniem „które ciało świeci",
   * i tak trzeba na nie odpowiedzieć jednym słowem przy wyborze barwy. To jest
   * pytanie „jak bardzo", i zadaje je wszystko, co ma **narastać** o zmierzchu
   * zamiast zapalać się w jednej klatce — dziś ogniska w osadach.
   */
  nightness: number;
}

const DAWN = new THREE.Color(0xffb066);
const NOON = new THREE.Color(0xfff4e0);
const DUSK = new THREE.Color(0xff8a4c);
const MOONLIGHT = new THREE.Color(0x9fb8e6);

const SKY_DAY = new THREE.Color(0xbfd8ff);
const SKY_DUSK = new THREE.Color(0xd08b6a);
const SKY_NIGHT = new THREE.Color(0x3b5080);

const GROUND_DAY = new THREE.Color(0x6b6552);
const GROUND_NIGHT = new THREE.Color(0x2a3348);

const FOG_DAY = new THREE.Color(0x87ceeb);
const FOG_DUSK = new THREE.Color(0xc4805e);
const FOG_NIGHT = new THREE.Color(0x1d2740);

/** How much of the cycle each dusk and dawn takes to cross over. */
const TWILIGHT = 0.02;

/** Smooth 0→1 across a band, flat outside it. */
function ramp(from: number, to: number, value: number): number {
  const t = THREE.MathUtils.clamp((value - from) / (to - from), 0, 1);
  return t * t * (3 - 2 * t);
}

/** Two skies mixed, `amount` of the way from the first to the second. */
function blend(from: SkyState, to: SkyState, amount: number): SkyState {
  const mix = (a: number, b: number) => a + (b - a) * amount;
  return {
    // Both lights sit near the horizon through a crossfade, so a straight lerp
    // of the directions passes near enough to the sky and never through zero.
    direction: from.direction.clone().lerp(to.direction, amount).normalize(),
    altitude: mix(from.altitude, to.altitude),
    sunColor: from.sunColor.clone().lerp(to.sunColor, amount),
    sunIntensity: mix(from.sunIntensity, to.sunIntensity),
    skyColor: from.skyColor.clone().lerp(to.skyColor, amount),
    groundColor: from.groundColor.clone().lerp(to.groundColor, amount),
    ambientIntensity: mix(from.ambientIntensity, to.ambientIntensity),
    fogColor: from.fogColor.clone().lerp(to.fogColor, amount),
    shadowStrength: mix(from.shadowStrength, to.shadowStrength),
    night: amount >= 0.5,
    nightness: mix(from.nightness, to.nightness),
  };
}

/**
 * A phase of the day as a 24-hour clock.
 *
 * Free of the class because two places say the time out loud and only one of
 * them has a sky: the game's debug line, and the player's page, which is shown
 * before there is a renderer at all. Sunrise and sunset are pinned to 6 and 18
 * whatever the split between day and night is, so the hours read the way hours
 * read even though night is a third of the day.
 */
export function clockOf(phase: number, nightShare: number): string {
  const daylight = 1 - nightShare;
  const at = ((phase % 1) + 1) % 1;

  const hours =
    at < daylight ? 6 + (at / daylight) * 12 : 18 + ((at - daylight) / nightShare) * 12;

  const whole = Math.floor(hours) % 24;
  const minutes = Math.floor((hours - Math.floor(hours)) * 60);
  return `${String(whole).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Whether that phase falls in the night. */
export function isNight(phase: number, nightShare: number): boolean {
  return ((phase % 1) + 1) % 1 >= 1 - nightShare;
}

export class Daylight {
  private phase: number;
  // Not readonly any more: the panel can move the sky, and `resync` is how a
  // client hears about it.
  private daySeconds: number;
  private nightShare: number;

  private paused = false;

  constructor(clock: DaylightClock) {
    this.phase = 0;
    this.daySeconds = 1;
    this.nightShare = 0.33;
    this.resync(clock);
  }

  /**
   * Take the clock from the server again.
   *
   * Called at the greeting and whenever the panel moves the sky. The phase is
   * replaced outright rather than eased towards: an administrator asking for
   * dusk wants dusk, not a sunset that takes a minute to arrive.
   */
  resync(clock: DaylightClock): void {
    this.phase = ((clock.phase % 1) + 1) % 1;
    this.daySeconds = Math.max(1, clock.daySeconds);
    this.nightShare = THREE.MathUtils.clamp(clock.nightShare, 0.05, 0.9);
    this.paused = clock.paused ?? false;
  }

  /** Move the clock on by a frame. Held skies do not move. */
  advance(seconds: number): void {
    if (this.paused) return;
    this.phase = (this.phase + seconds / this.daySeconds) % 1;
  }

  /** Whether the day is standing still because somebody pinned it. */
  get held(): boolean {
    return this.paused;
  }

  /** Where in the day we are, for anything that wants to say it out loud. */
  get at(): number {
    return this.phase;
  }

  /** The time as a 24-hour clock, with sunrise at 6:00 and sunset at 18:00. */
  get clock(): string {
    return clockOf(this.phase, this.nightShare);
  }

  /**
   * Everything the renderer needs, for the phase the clock is at now.
   *
   * Sun and moon are worked out separately and **crossfaded**, rather than
   * switched between at the moment of sunset. Switching measured a jump of 0,31
   * in the light over ten seconds — a visible flick at exactly the moment a day
   * cycle is there to be looked at. The fade is `TWILIGHT` of the cycle either
   * side of the boundary, some three and a half minutes at a three-hour day,
   * and both lights are near the horizon throughout it, so what is mixed are
   * two nearly identical geometries.
   */
  state(): SkyState {
    const daylight = 1 - this.nightShare;

    // Zero in full day, one in full night, sliding across the two boundaries.
    const night = this.nightness();

    // Which end of its own arc each light is on has to be the near one: at dawn
    // the moon is **setting**, not rising, and reading it as rising swung the
    // mixed direction through half the sky — measured at 108° of travel in ten
    // seconds. Each light is therefore taken forwards inside its own night or
    // day and backwards outside it. The switch happens where the fade weight is
    // still zero, so it is never seen.
    const dusk = this.phase > daylight - TWILIGHT;
    const sun = this.light(
      false,
      THREE.MathUtils.clamp((this.phase < daylight + TWILIGHT ? this.phase : this.phase - 1) / daylight, 0, 1)
    );
    const moon = this.light(
      true,
      THREE.MathUtils.clamp(
        ((dusk ? this.phase : this.phase + 1) - daylight) / this.nightShare,
        0,
        1
      )
    );

    if (night <= 0) return sun;
    if (night >= 1) return moon;
    return blend(sun, moon, night);
  }

  /** How far into night we are, with a fade across each boundary. */
  private nightness(): number {
    const daylight = 1 - this.nightShare;

    if (this.phase < daylight - TWILIGHT) {
      return this.phase < TWILIGHT ? 1 - ramp(-TWILIGHT, TWILIGHT, this.phase) : 0;
    }
    if (this.phase < daylight + TWILIGHT) {
      return ramp(daylight - TWILIGHT, daylight + TWILIGHT, this.phase);
    }
    if (this.phase < 1 - TWILIGHT) return 1;
    return 1 - ramp(1 - TWILIGHT, 1 + TWILIGHT, this.phase);
  }

  /** One light on its own arc, as if it were the only one in the sky. */
  private light(night: boolean, across: number): SkyState {
    const peak = night ? MOON_PEAK : SUN_PEAK;
    const altitude = Math.sin(across * Math.PI) * peak;

    // The moon crosses the opposite half of the sky, so night is lit from the
    // other side and does not read as a dim afternoon.
    const azimuth = SUN_START + across * SUN_SWEEP + (night ? 180 : 0);

    const up = THREE.MathUtils.degToRad(altitude);
    const round = THREE.MathUtils.degToRad(azimuth);
    const direction = new THREE.Vector3(
      Math.cos(up) * Math.cos(round),
      Math.sin(up),
      Math.cos(up) * Math.sin(round)
    );

    // Warm at both ends of the arc, white in the middle of it.
    const high = ramp(0, 0.35, Math.min(across, 1 - across) * 2);
    const sunColor = night
      ? MOONLIGHT.clone()
      : (across < 0.5 ? DAWN : DUSK).clone().lerp(NOON, high);

    // Intensity follows the altitude rather than the clock, so the light fades
    // as the sun comes down instead of stopping at a time.
    const height = ramp(0, 0.5, Math.sin(across * Math.PI));
    const sunIntensity = night ? 0.24 + 0.14 * height : 0.35 + 0.75 * height;

    const dusk = 1 - high;
    const skyColor = night
      ? SKY_NIGHT.clone()
      : SKY_DAY.clone().lerp(SKY_DUSK, dusk * 0.8);
    const groundColor = night
      ? GROUND_NIGHT.clone()
      : GROUND_DAY.clone().lerp(GROUND_NIGHT, dusk * 0.5);

    // Night keeps a floor under it, and the floor has been raised once: the game
    // draws into a small buffer scaled up whole, and darkness there is not
    // atmosphere — it is a picture with no information in it. The moon now
    // gives 0,24–0,38 over an ambient of 0,52, against 0,18–0,30 over 0,42 to
    // begin with, and the night sky and fog were lifted with it.
    const ambientIntensity = night ? 0.52 : 0.34 + 0.22 * (1 - high);

    const fogColor = night
      ? FOG_NIGHT.clone()
      : FOG_DAY.clone().lerp(FOG_DUSK, dusk * 0.85);

    return {
      direction,
      altitude,
      sunColor,
      sunIntensity,
      skyColor,
      groundColor,
      ambientIntensity,
      fogColor,
      shadowStrength: ramp(0, SHADOW_FADE_DEGREES, altitude),
      night,
      // Pojedyncze ciało jest w całości dniem albo w całości nocą; przejście
      // między nimi robi `blend`, mieszając te dwie jedynki.
      nightness: night ? 1 : 0,
    };
  }
}
